/**
 * Session Token Processor
 *
 * Handler centralizado para processar tokens de sessão (tools:session-token).
 * Movido de ToolsMenu.tsx para reutilização.
 *
 * @author Simplificação Fase 1 - 2026-01-13
 */

import { sendToTerminal } from '@/lib/terminalLogger';
import { TokenValidator } from '@/lib/corel/tokenValidator';
import { corelCommunication } from '@/lib/corel/communication';

export interface SessionTokenProcessingResult {
  success: boolean;
  tokenStored: boolean;
  hmacStored: boolean;
  dockerId?: string;
  sessionEpoch: number;
  tokenVersion: number;
}

/**
 * Handler centralizado para processar tokens de sessão (tools:session-token)
 * Simplifica e remove duplicação entre webview e window handlers
 *
 * @param sessionToken Token de sessão recebido
 * @param hmacKeyB64 Chave HMAC em base64 recebida
 * @param source Origem da mensagem ('webview' ou 'window')
 */
export function processSessionToken(
  sessionToken: string | undefined,
  hmacKeyB64: string | undefined,
  source: 'webview' | 'window',
  metadata?: {
    dockerId?: string;
    sessionEpoch?: number;
    tokenVersion?: number;
  }
): SessionTokenProcessingResult {
  const sourceLabel = source === 'webview' ? 'DOCKER_TOOLS' : 'WINDOW:MSG';
  const timestamp = Date.now();
  const currentEpoch = Number((window as any).sessionEpoch || 0);
  const currentTokenVersion = Number((window as any).tokenVersion || 0);
  const currentDockerId = String((window as any).dockerId || '');
  const nextEpoch = Number(metadata?.sessionEpoch || 0);
  const nextTokenVersion = Number(metadata?.tokenVersion || 0);
  let tokenStored = false;
  let hmacStored = false;

  if (nextEpoch > 0 && currentEpoch > nextEpoch) {
    sendToTerminal(`⏭️ [${sourceLabel}] Bootstrap descartado por sessionEpoch antigo`, {
      currentEpoch,
      nextEpoch,
      dockerId: metadata?.dockerId
    });
    return {
      success: false,
      tokenStored: false,
      hmacStored: false,
      dockerId: metadata?.dockerId,
      sessionEpoch: nextEpoch,
      tokenVersion: nextTokenVersion
    };
  }

  if (nextEpoch > 0 && currentEpoch === nextEpoch && nextTokenVersion > 0 && currentTokenVersion >= nextTokenVersion) {
    sendToTerminal(`⏭️ [${sourceLabel}] Bootstrap descartado por tokenVersion antigo`, {
      currentTokenVersion,
      nextTokenVersion,
      dockerId: metadata?.dockerId
    });
    return {
      success: false,
      tokenStored: false,
      hmacStored: false,
      dockerId: metadata?.dockerId,
      sessionEpoch: nextEpoch,
      tokenVersion: nextTokenVersion
    };
  }

  // Validar tokens usando TokenValidator
  const tokenValidation = TokenValidator.validateSessionToken(sessionToken);
  const hmacValidation = TokenValidator.validateHmacKey(hmacKeyB64);

  // Log de diagnóstico
  sendToTerminal(`🔑 [${sourceLabel}] Recebido tools:session-token`, {
    hasSessionToken: !!sessionToken,
    hasHmacKey: !!hmacKeyB64,
    dockerId: metadata?.dockerId,
    sessionEpoch: nextEpoch,
    tokenVersion: nextTokenVersion,
    tokenValid: tokenValidation.valid,
    tokenFormat: tokenValidation.format || 'invalid',
    tokenLength: tokenValidation.length || 0,
    hmacValid: hmacValidation.valid,
    hmacLength: hmacValidation.length || 0,
    timestamp,
    tokenPreview: TokenValidator.createPreview(sessionToken),
    hmacPreview: TokenValidator.createPreview(hmacKeyB64)
  });

  // Processar sessionToken
  if (!sessionToken || !tokenValidation.valid) {
    sendToTerminal(`⚠️ [${sourceLabel}] Token inválido - tentando fallback DOM`, {
      error: tokenValidation.error
    });

    // Fallback: tentar ler do DOM
    const domToken = (window as any)?.sessionToken;
    const domValidation = TokenValidator.validateSessionToken(domToken);

    if (domValidation.valid) {
      sendToTerminal(`✅ [${sourceLabel}] Token encontrado no DOM`, {
        tokenFormat: domValidation.format,
        tokenLength: domValidation.length
      });
      corelCommunication.setSessionToken(domToken);
      tokenStored = true;
    } else {
      sendToTerminal(`❌ [${sourceLabel}] Token ausente/inválido (mensagem E DOM)`, {
        domTokenError: domValidation.error
      });
    }
  } else {
    // Token válido - salvar
    try {
      (window as any).sessionToken = sessionToken;
      if (nextEpoch > 0) {
        (window as any).sessionEpoch = nextEpoch;
      }
      if (nextTokenVersion > 0) {
        (window as any).tokenVersion = nextTokenVersion;
      }
      if (!currentDockerId && metadata?.dockerId) {
        (window as any).dockerId = metadata.dockerId;
        sendToTerminal(`✅ [${sourceLabel}] dockerId inicializado a partir do bootstrap`, {
          dockerId: metadata.dockerId,
          reason: 'window-dockerid-empty'
        });
      } else if (metadata?.dockerId && currentDockerId && currentDockerId !== metadata.dockerId) {
        sendToTerminal(`ℹ️ [${sourceLabel}] dockerId do bootstrap ignorado`, {
          currentDockerId,
          bootstrapDockerId: metadata.dockerId,
          reason: 'window-dockerid-already-set'
        });
      }
      corelCommunication.setSessionToken(sessionToken);
      tokenStored = true;
      sendToTerminal(`✅ [${sourceLabel}] Token salvo e configurado`, {
        tokenFormat: tokenValidation.format,
        tokenLength: tokenValidation.length
      });
    } catch (err) {
      sendToTerminal(`❌ [${sourceLabel}] Erro ao salvar token`, {
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }

  // Processar HMAC key
  if (!hmacKeyB64 || !hmacValidation.valid) {
    sendToTerminal(`⚠️ [${sourceLabel}] HMAC key inválida`, {
      error: hmacValidation.error
    });
  } else {
    try {
      if (!(window as any).sessionCrypto) {
        (window as any).sessionCrypto = {};
      }
      (window as any).sessionCrypto.hmacKeyB64 = hmacKeyB64;
      if (nextEpoch > 0) {
        (window as any).sessionEpoch = nextEpoch;
      }
      if (nextTokenVersion > 0) {
        (window as any).tokenVersion = nextTokenVersion;
      }
      corelCommunication.setHmacSecret(hmacKeyB64);
      hmacStored = true;
      sendToTerminal(`✅ [${sourceLabel}] HMAC key salva e configurada`, {
        hmacLength: hmacValidation.length
      });
    } catch (err) {
      sendToTerminal(`❌ [${sourceLabel}] Erro ao salvar HMAC key`, {
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }

  // Diagnóstico final
  const finalDiagnostics = TokenValidator.getDiagnostics();
  sendToTerminal(`📊 [${sourceLabel}] Estado final após processamento`, {
    sessionToken: finalDiagnostics.sessionToken,
    hmacKey: finalDiagnostics.hmacKey,
    success: finalDiagnostics.sessionToken.valid && finalDiagnostics.hmacKey.valid
  });

  return {
    success: finalDiagnostics.sessionToken.valid && finalDiagnostics.hmacKey.valid,
    tokenStored,
    hmacStored,
    dockerId: metadata?.dockerId,
    sessionEpoch: nextEpoch,
    tokenVersion: nextTokenVersion
  };
}
